import React, { useEffect, useState } from "react";
import {
  Sparkles, Zap, ListChecks, CalendarClock, ShieldAlert, FileText,
  Briefcase, Bell, ArrowRight, CheckCircle2, Pencil, MailPlus,
} from "lucide-react";

/**
 * DealKickoffCelebration — animated reveal of everything ChainFlow just
 * did automatically when a deal was created. Renders inline inside the
 * CreateDealModal once the kickoff response arrives.
 *
 * The data is real (server-side); the staging is purely theatrical so the
 * agent feels the value of the automation. Each step reveals on a delay
 * with a checkmark + the actual count from the kickoff summary.
 */
const REVEAL_STEPS = [
  { key: "tasks",        icon: ListChecks,    label: "Default tasks created",     hint: "AML · solicitors · memo · mortgage · searches · risk review" },
  { key: "milestones",   icon: CalendarClock, label: "Milestone deadlines set",   hint: "AML cleared · memo issued · mortgage offer · searches · exchange · completion" },
  { key: "memo",         icon: FileText,      label: "Memorandum draft generated", hint: "Auto-populated with property, price, parties + ready to send" },
  { key: "risk",         icon: ShieldAlert,   label: "Risk score computed",       hint: "Heuristic across solicitors, broker, chain health + price" },
  { key: "solicitors",   icon: Briefcase,     label: "Solicitor placeholders pinned", hint: "Track open positions until both sides are instructed" },
  /* P1 · Controlled Participant Invitations (2026-02-28) —
   * The notification step reads as INTERNAL ONLY so the operator
   * never mistakes the kickoff for an external invitation blast.
   * Participants remain in a "Not Invited" state; invitations
   * are dispatched deliberately from the Deal Room's participants
   * panel where the existing invitation infrastructure lives. */
  { key: "notifications", icon: Bell,          label: "Internal workspace briefed", hint: "Your team sees this in the activity centre · participants are not contacted" },
  { key: "room",         icon: Sparkles,      label: "Deal Room ready",           hint: "Sales Progression, Documents, Messenger and Activity all wired" },
];

const stagger = (i) => 220 + i * 240; // ms — feels deliberate, not slow

export const DealKickoffCelebration = ({ summary, deal, onOpenRoom, onContinueEditing, onInviteParticipants }) => {
  const [revealed, setRevealed] = useState(0);

  useEffect(() => {
    if (!summary) return;
    const timers = REVEAL_STEPS.map((_, i) =>
      setTimeout(() => setRevealed((n) => Math.max(n, i + 1)), stagger(i))
    );
    return () => timers.forEach((t) => clearTimeout(t));
  }, [summary]);

  if (!summary) return null;

  const counts = {
    tasks:        summary.tasks_created,
    milestones:   summary.milestones_created,
    memo:         summary.documents_created,
    risk:         summary.risk?.level || "—",
    solicitors:   (summary.solicitor_placeholders || []).length,
    notifications: summary.notifications_sent,
    room:         "Live",
  };
  const finished = revealed >= REVEAL_STEPS.length;
  const totalSeconds = Math.round(stagger(REVEAL_STEPS.length - 1) / 1000);

  return (
    <div className="cf-fade-in" data-testid="deal-kickoff-celebration">
      {/* Hero */}
      <div className="relative pt-2 pb-5 text-center">
        <div className="absolute -inset-x-12 -top-8 h-44 cf-blob opacity-30 pointer-events-none" />
        <div className="relative inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-indigo-50 border border-indigo-200 text-indigo-700 text-[10.5px] font-semibold uppercase tracking-[0.22em]">
          <Zap size={11} className="fill-indigo-600" /> ChainFlow Kickoff
        </div>
        <h3 className="font-display text-[26px] md:text-[30px] font-bold tracking-[-0.02em] text-slate-900 mt-3 leading-[1.1]">
          We just did 45 minutes of admin in {totalSeconds} seconds
        </h3>
        <p className="text-[13px] text-slate-500 mt-2 max-w-[480px] mx-auto leading-snug">
          Your new deal is provisioned — tasks scheduled, memo drafted, risks scored, deadlines pinned and the team notified.
        </p>
      </div>

      {/* Reveal list */}
      <div className="rounded-2xl bg-white border border-slate-200/80 overflow-hidden shadow-[0_4px_18px_-6px_rgba(15,23,42,0.06)]">
        {REVEAL_STEPS.map((s, i) => {
          const Icon = s.icon;
          const isShown = revealed > i;
          const isCurrent = revealed === i + 1 && !finished;
          const value = counts[s.key];
          return (
            <div
              key={s.key}
              className={`flex items-start gap-3 px-4 py-3 border-b border-slate-100 last:border-b-0 transition-all duration-500 ease-out ${
                isShown ? "opacity-100 translate-y-0" : "opacity-0 translate-y-1.5"
              }`}
              data-testid={`kickoff-step-${s.key}`}
            >
              <span
                className={`w-9 h-9 shrink-0 rounded-xl flex items-center justify-center transition-all duration-500 ${
                  isShown
                    ? "bg-gradient-to-br from-indigo-500 to-indigo-700 text-white shadow-[0_8px_18px_-6px_rgba(99, 102, 241,0.55)]"
                    : "bg-slate-100 text-slate-400"
                } ${isCurrent ? "ring-2 ring-indigo-200 cf-pulse" : ""}`}
              >
                {isShown ? <CheckCircle2 size={16} /> : <Icon size={15} />}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-3">
                  <p className={`text-[13.5px] font-semibold transition-colors ${isShown ? "text-slate-900" : "text-slate-400"}`}>
                    {s.label}
                  </p>
                  {isShown && (
                    <span className="text-[11px] font-bold cf-num px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100 cf-fade-up">
                      {typeof value === "number" ? `${value}` : value}
                    </span>
                  )}
                </div>
                <p className={`text-[11.5px] mt-0.5 leading-snug transition-colors ${isShown ? "text-slate-500" : "text-slate-300"}`}>
                  {s.hint}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Risk highlight when reveal completes */}
      {finished && summary.risk && (
        <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3 cf-fade-up" data-testid="kickoff-risk-summary">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <ShieldAlert
                size={14}
                className={
                  summary.risk.level === "High" ? "text-rose-600"
                  : summary.risk.level === "Medium" ? "text-amber-600"
                  : "text-emerald-600"
                }
              />
              <span className="text-[12px] font-semibold text-slate-900">
                Initial risk: {summary.risk.level} · {summary.risk.score}/100
              </span>
            </div>
            <span className="text-[10.5px] text-slate-500 hidden sm:inline">
              {summary.risk.reasons?.length || 0} flag{(summary.risk.reasons?.length || 0) === 1 ? "" : "s"} to address
            </span>
          </div>
          {summary.risk.reasons?.length > 0 && (
            <ul className="mt-2 space-y-0.5">
              {summary.risk.reasons.slice(0, 3).map((r) => (
                <li key={r} className="text-[11.5px] text-slate-600 leading-snug">• {r}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* P1 · Controlled Participant Invitations (2026-02-28)
          Success-state CTAs.  Three deliberate actions replace
          the previous single "Open Deal Room" button so the
          operator can (a) keep configuring the room, (b) fire
          off invitations deliberately, or (c) open the room to
          continue work.  Every CTA remains disabled until the
          kickoff reveal completes so the operator sees the full
          "what just happened" story before acting. */}
      <div
        className="mt-5 flex flex-wrap items-center justify-center gap-2.5"
        data-testid="kickoff-cta-row"
      >
        <button
          type="button"
          onClick={onContinueEditing}
          disabled={!finished}
          className={`inline-flex items-center gap-2 h-10 px-4 rounded-xl text-[12.5px] font-semibold bg-white ring-1 ring-slate-200 text-slate-700 hover:bg-slate-50 hover:text-slate-900 transition-colors ${
            finished ? "" : "opacity-60 cursor-not-allowed"
          }`}
          data-testid="kickoff-continue-editing"
        >
          <Pencil size={12} /> Continue editing
        </button>
        <button
          type="button"
          onClick={onInviteParticipants}
          disabled={!finished}
          className={`inline-flex items-center gap-2 h-10 px-4 rounded-xl text-[12.5px] font-semibold bg-emerald-50 ring-1 ring-emerald-200 text-emerald-800 hover:bg-emerald-100 transition-colors ${
            finished ? "" : "opacity-60 cursor-not-allowed"
          }`}
          data-testid="kickoff-invite-participants"
        >
          <MailPlus size={12} /> Invite participants
        </button>
        <button
          type="button"
          onClick={onOpenRoom}
          disabled={!finished}
          className={`inline-flex items-center gap-2 h-11 px-5 rounded-xl text-white text-[13px] font-semibold transition-all ${
            finished ? "" : "opacity-60 cursor-not-allowed"
          }`}
          style={{
            background: "linear-gradient(180deg, #6366F1 0%, #4F6BFF 100%)",
            boxShadow: "0 14px 30px -12px rgba(99, 102, 241,0.55), inset 0 1px 0 rgba(255,255,255,0.18)",
          }}
          data-testid="kickoff-open-room"
        >
          <Sparkles size={13} /> Open Deal Room <ArrowRight size={13} />
        </button>
      </div>
    </div>
  );
};
